/**
 * Payment Session Blocking Matrix
 *
 * Pure functions for determining payment tab visibility based on active sessions.
 *
 * Blocking Matrix:
 * | Active Type | Full | Partial | Plan |
 * |-------------|------|---------|------|
 * | Full        | ❌   | ❌      | ❌   |
 * | Partial     | ❌   | ✅      | ❌   |
 * | Plan        | ✅   | ✅      | ✅   |
 *
 * Business Rationale:
 * - Full payment = Exclusive commitment to clear entire debt → Block all other activity
 * - Partial payment = Incremental payments → Allow more partials, block commitments (full/plan)
 * - Payment plan = Scheduled commitment already made → Customer can supplement with any payment
 */

import type { PaymentSessionDto } from '@/lib/connect-api';

export type PaymentType = 'full' | 'partial' | 'payment_plan';

export interface PaymentTabVisibility {
  /**
   * Whether full payment tab should be shown
   */
  canPayFull: boolean;

  /**
   * Whether partial payment tab should be shown
   */
  canPayPartial: boolean;

  /**
   * Whether payment plan tab should be shown
   */
  canCreatePlan: boolean;
}

/**
 * Determine which payment tabs should be visible based on active session
 *
 * @param activeSession - Current active session (or null if none)
 * @returns Object with tab visibility flags
 *
 * @example
 * ```ts
 * const visibility = getPaymentTabVisibility(activeSession);
 * if (!visibility.canPayFull) {
 *   // Hide full payment tab
 * }
 * ```
 */
export function getPaymentTabVisibility(activeSession: PaymentSessionDto | null): PaymentTabVisibility {
  // No active session → Allow all payment types
  if (!activeSession) {
    return {
      canPayFull: true,
      canPayPartial: true,
      canCreatePlan: true,
    };
  }

  const activeType = activeSession.payment_type;

  // Full payment active → Block ALL tabs
  if (activeType === 'full') {
    return {
      canPayFull: false,
      canPayPartial: false,
      canCreatePlan: false,
    };
  }

  // Partial payment active → Block full and plan, allow partial
  if (activeType === 'partial') {
    return {
      canPayFull: false,
      canPayPartial: true,
      canCreatePlan: false,
    };
  }

  // Payment plan active → Allow ALL tabs
  if (activeType === 'payment_plan') {
    return {
      canPayFull: true,
      canPayPartial: true,
      canCreatePlan: true,
    };
  }

  // Fallback: Unknown payment type → Allow all (defensive)
  console.warn('[getPaymentTabVisibility] Unknown payment type:', activeType);
  return {
    canPayFull: true,
    canPayPartial: true,
    canCreatePlan: true,
  };
}
